import { useEffect, useState } from 'react';
import { Download, ChevronDown, Search, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../utils/api';
import { toast } from 'sonner';
import { format, parse } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import EnhancedDateSelector from '../components/EnhancedDateSelector';

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null, label: 'All Time' });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Pagination state (server-side)
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // KPI state - need to fetch separately or calculate from all data
  const [kpiData, setKpiData] = useState({
    cashDeposits: 0,
    netRemittances: 0,
    totalPurchasePrice: 0,
    depositsInOctober: 0,
    mcaSelfDirected: 0
  });

  useEffect(() => {
    fetchTransactions();
    fetchKPIData(); // Fetch KPIs separately
  }, [currentPage, itemsPerPage]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      console.log(`Fetching transactions page ${currentPage} with limit ${itemsPerPage}...`);
      const response = await api.get(`/transactions?page=${currentPage}&limit=${itemsPerPage}`);
      console.log('Transactions received:', response.data.data.length, 'records');
      console.log('Total transactions:', response.data.total);
      setTransactions(response.data.data);
      setTotalTransactions(response.data.total);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchKPIData = async () => {
    try {
      // Fetch KPIs from dedicated endpoint
      const response = await api.get('/transactions/kpis');
      
      setKpiData({
        cashDeposits: response.data.cash_deposits || 0,
        netRemittances: response.data.net_remittances || 0,
        totalPurchasePrice: response.data.total_purchase_price || 0,
        depositsInOctober: response.data.deposits_in_october || 0,
        mcaSelfDirected: response.data.mca_self_directed || 0
      });
    } catch (error) {
      console.error('Failed to fetch KPI data:', error);
    }
  };

  // Get available years from transaction data
  const getAvailableYears = () => {
    const years = new Set();
    transactions.forEach(txn => {
      const scheduledDate = new Date(txn['Scheduled For (datetime)'] || txn['Scheduled For']);
      if (!isNaN(scheduledDate)) {
        years.add(scheduledDate.getFullYear());
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  };

  const availableYears = getAvailableYears();

  // Handle date range change from enhanced selector
  const handleDateRangeChange = (range) => {
    setDateRange(range);
  };

  const clearAllFilters = () => {
    setDateRange({ startDate: null, endDate: null, label: 'All Time' });
    toast.success('All filters cleared');
  };

  const exportToCSV = async () => {
    try {
      toast.info('Preparing export... This may take a moment');
      // Fetch all transactions for export (use very large limit)
      const response = await api.get('/transactions?page=1&limit=100000');
      const allTransactions = response.data.data;
      
      if (allTransactions.length === 0) {
        toast.error('No transactions to export');
        return;
      }
      
      const headers = Object.keys(allTransactions[0]);
      const rows = allTransactions.map((t) => 
        headers.map(header => {
          const value = t[header] || '';
          // Escape commas and quotes in CSV
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        })
      );

      const csvContent =
        'data:text/csv;charset=utf-8,' +
        [headers.join(','), ...rows.map(row => row.join(','))].join('\n');

      const link = document.createElement('a');
      link.setAttribute('href', encodeURI(csvContent));
      link.setAttribute('download', `transactions_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Exported ${allTransactions.length} transactions to CSV`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export transactions');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Loading Indicator with Spinner */}
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
            <h3 className="text-xl font-semibold text-slate-700 mb-2">Loading Transactions...</h3>
            <p className="text-slate-500">Retrieving your transaction history</p>
          </div>
        </div>
        
        {/* Skeleton Table */}
        <div className="bg-white rounded-xl p-6 shadow-sm animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-48 mb-6"></div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="h-12 bg-slate-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Search and filter transactions
  const filteredTransactions = transactions.filter(transaction => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return Object.values(transaction).some(value => 
      value && value.toString().toLowerCase().includes(query)
    );
  });

  // Sort transactions
  const sortedTransactions = [...filteredTransactions].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    
    // Handle null/undefined values
    if (aValue == null) return 1;
    if (bValue == null) return -1;
    
    // Handle numeric values
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    // Handle string values
    const aStr = aValue.toString().toLowerCase();
    const bStr = bValue.toString().toLowerCase();
    
    if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="w-4 h-4 ml-1 text-slate-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1 text-blue-600" />
      : <ArrowDown className="w-4 h-4 ml-1 text-blue-600" />;
  };

  // Pagination calculations for server-side
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + sortedTransactions.length, sortedTransactions.length);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1); // Reset to first page
  };

  return (
    <div className="space-y-0" data-testid="transactions-page">
      {/* Blue Header Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 -mx-6 -mt-8 px-6 pt-8 pb-16 mb-8">
        <h1 className="text-4xl font-bold text-white">Transactions</h1>
      </div>

      {/* Top KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 -mt-12 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-lg">
          <div className="text-sm text-slate-600 mb-2 flex items-center">
            Cash Deposits
            <span className="ml-2 text-slate-400 cursor-help text-lg" title="Total amount of cash deposited into your investment account from all deposit transactions">ⓘ</span>
          </div>
          <div className="text-3xl font-bold text-slate-900">${kpiData.cashDeposits.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
          <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t">
            <div>
              <div className="text-xs text-slate-500">Deposits in October</div>
              <div className="text-sm font-semibold">${kpiData.depositsInOctober.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Scheduled Deposits</div>
              <div className="text-sm font-semibold">0 | $0.00</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg">
          <div className="text-sm text-slate-600 mb-2 flex items-center">
            Net Remittances
            <span className="ml-2 text-slate-400 cursor-help text-lg" title="Total remittances/returns received from your investments. This is the cash flow coming back to you from merchant repayments">ⓘ</span>
          </div>
          <div className="text-3xl font-bold text-slate-900">${kpiData.netRemittances.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg">
          <div className="text-sm text-slate-600 mb-2 flex items-center">
            Total Purchase Price
            <span className="ml-2 text-slate-400 cursor-help text-lg" title="Sum of all investment purchase prices - total capital currently deployed across all investments">ⓘ</span>
          </div>
          <div className="text-3xl font-bold text-slate-900">${kpiData.totalPurchasePrice.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
        </div>
      </div>

      {/* Main Content - Transactions */}
      <div className="bg-white rounded-xl shadow-sm">
        {/* Filters Row */}
        <div className="p-6 border-b space-y-4">
          {/* Search Bar */}
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <Button
              onClick={exportToCSV}
              variant="outline"
              disabled={transactions.length === 0}
              className="flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </Button>
          </div>

          {/* Date Filter */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <EnhancedDateSelector
              onDateRangeChange={handleDateRangeChange}
              availableYears={availableYears}
              className="flex-1"
            />
            <div className="flex items-center gap-3">
              <div className="text-sm font-medium text-slate-700 bg-slate-50 px-4 py-2 rounded-lg border border-slate-200">
                <span className="text-blue-600">{dateRange.label}</span>
              </div>
              {searchQuery && (
                <div className="text-sm text-slate-600 bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
                  {filteredTransactions.length} results
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Transactions Table */}
        <>
          <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider sticky left-0 bg-slate-50 z-10">#</th>
                    {transactions.length > 0 && Object.keys(transactions[0]).map((key) => (
                      <th 
                        key={key} 
                        className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => handleSort(key)}
                      >
                        <div className="flex items-center">
                          {key}
                          {getSortIcon(key)}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {sortedTransactions.length > 0 ? (
                    sortedTransactions.map((transaction, index) => (
                      <tr key={index} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 sticky left-0 bg-white">{startIndex + index + 1}</td>
                        {Object.entries(transaction).map(([key, value]) => (
                          <td key={key} className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                            {typeof value === 'number' && key.toLowerCase().includes('amount') 
                              ? `$${value.toLocaleString()}` 
                              : value || 'N/A'}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="31" className="px-6 py-12 text-center text-slate-500">
                        <p className="text-lg font-medium">No transactions found</p>
                        <p className="text-sm mt-1">{searchQuery ? 'Try adjusting your search' : 'Try adjusting your filters'}</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            {sortedTransactions.length > 0 && (
              <div className="flex items-center justify-between px-6 py-4 border-t">
                <div className="text-sm text-slate-600">
                  Showing {startIndex + 1} to {Math.min(endIndex, sortedTransactions.length)} of {sortedTransactions.length} transactions
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-slate-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                    className="px-2 py-1 border rounded text-sm"
                  >
                    <option value="10">10 per page</option>
                    <option value="50">50 per page</option>
                    <option value="100">100 per page</option>
                    <option value="250">250 per page</option>
                    <option value="500">500 per page</option>
                  </select>
                </div>
              </div>
            )}
          </>
      </div>
    </div>
  );
};

export default Transactions;